import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  IdcardOutlined,
  LoadingOutlined,
  LockOutlined,
  CreditCardOutlined,
  PlusOutlined,
  MinusOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Spin, Row, Col, Modal, Skeleton } from "antd";
import Swal from "sweetalert2";
import PagesHeader from "../../../components/PageHeader";
import ButtonBar from "../../../components/ButtonNavigation/index";
import * as SubscriptionService from "../../../services/InstallerSubscriptionService";
import * as InstallerData from "../../../services/InstallerData";

const ChooseInstallerSeats = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode"); // "add_more" or null

  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [seatStatus, setSeatStatus] = useState(null);
  const [seatCount, setSeatCount] = useState(mode === "add_more" ? 1 : 3);

  useEffect(() => {
    fetchInitialData();
    window.scrollTo(0, 0);
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [plansRes, installersRes] = await Promise.allSettled([
        dispatch(SubscriptionService.getInstallerPlans()),
        dispatch(InstallerData.getInstallerListing()),
      ]);

      const usedCount = installersRes.status === "fulfilled" && Array.isArray(installersRes.value)
        ? installersRes.value.length
        : 0;

      const statusRes = await dispatch(SubscriptionService.getInstallerSeatStatus(usedCount));
      setSeatStatus(statusRes);

      if (plansRes.status === "fulfilled") {
        const plansList = plansRes.value?.plans || (Array.isArray(plansRes.value) ? plansRes.value : []);
        const activePlan = plansList.find((p) => p.is_active) || plansList[0];
        setPlan(activePlan);
      }
    } catch (err) {
      console.error("Error loading subscription data", err);
    } finally {
      setLoading(false);
    }
  };

  const unitPrice = parseFloat(plan?.price || 20);
  const totalPrice = (seatCount * unitPrice).toFixed(2);
  const isAddMoreMode = mode === "add_more";

  const handleIncrement = () => {
    setSeatCount((prev) => prev + 1);
  };

  const handleDecrement = () => {
    setSeatCount((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleInputChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) {
      setSeatCount(1);
    } else {
      setSeatCount(val);
    }
  };

  const handleProceedToCheckout = () => {
    if (!plan?.id) {
      Swal.fire({
        icon: "error",
        title: "Plan Not Found",
        text: "Could not retrieve plan information. Please try again.",
      });
      return;
    }

    setCheckoutLoading(true);
    dispatch(SubscriptionService.subscribeInstallerSeats(plan.id, seatCount))
      .then((res) => {
        if (res?.checkout_url) {
          window.location.href = res.checkout_url;
        } else {
          setCheckoutLoading(false);
          Swal.fire({
            icon: "error",
            title: "Checkout Error",
            text: res?.message || "Failed to generate checkout session. Please try again.",
          });
        }
      })
      .catch((err) => {
        setCheckoutLoading(false);
        Swal.fire({
          icon: "error",
          title: "Payment Error",
          text: err?.response?.data?.message || "Something went wrong while connecting to checkout.",
        });
      });
  };

  // Find renews_at or ends_at
  let renewsOn = "-";
  if (seatStatus?.subscriptions && seatStatus.subscriptions.length > 0) {
    const activeSub = seatStatus.subscriptions.find((s) => s.renews_at) || seatStatus.subscriptions[0];
    renewsOn = activeSub?.renews_at || activeSub?.ends_at || "-";
  }

  return (
    <div className="inspectionWrapper">
      <div className="inspectionHeader">
        <PagesHeader title={isAddMoreMode ? "Add More Installer Pro Access Seats" : "Installer Pro Access Subscription"} />
      </div>

      <div className="buttonSection">
        <div className="container-xxl" style={{ paddingLeft: "32px", paddingRight: "32px" }}>
          <Row>
            <Col xs={24} md={24} className="text-end">
              <ButtonBar />
            </Col>
          </Row>
        </div>
      </div>

      <div className="inspectionWrapInner" style={{ padding: "20px 0 40px" }}>
        <div className="container-xxl" style={{ paddingLeft: "32px", paddingRight: "32px" }}>
          <div className="subscription-flow-page">
            <div className="flow-content-centered">
              {/* Back Navigation */}
              <div
                className="back-nav-btn"
                onClick={() =>
                  navigate(isAddMoreMode ? "/installer/manage-subscription" : "/all-installers")
                }
              >
                <ArrowLeftOutlined />
                <span>{isAddMoreMode ? "Back to Subscription" : "Back to Installers"}</span>
              </div>

              {/* Heading */}
              <div className="flow-header">
                <h2>{isAddMoreMode ? "Add More Seats" : "Choose Your Installer Pro Access Seats"}</h2>
                <p>
                  {isAddMoreMode
                    ? "Scale your installer capacity effortlessly. Extra seats will be added to your current team quota."
                    : "Select the number of certified installer seats needed for your team to unlock installer management."}
                </p>
              </div>

              {loading ? (
                <div className="flow-cards-wrapper">
                  {/* Left Card: Plan Details Skeleton */}
                  <div className="plan-info-card" style={{ cursor: "default" }}>
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <span className="plan-badge-top" style={{ background: "transparent", border: "none", padding: 0 }}>
                          <Skeleton.Button active size="small" style={{ width: 175, height: 24, borderRadius: 20 }} />
                        </span>
                      </div>

                      <div className="plan-header">
                        <div className="plan-icon" style={{ background: "transparent", boxShadow: "none" }}>
                          <Skeleton.Avatar active shape="square" size={48} style={{ borderRadius: 12 }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: 6 }}>
                            <Skeleton.Input active size="small" style={{ width: 135, height: 18 }} />
                          </div>
                          <div>
                            <Skeleton.Input active size="small" style={{ width: 200, height: 13 }} />
                          </div>
                        </div>
                      </div>

                      <ul className="feature-list">
                        <li>
                          <span className="check-icon-circle" style={{ background: "transparent" }}>
                            <Skeleton.Avatar active shape="circle" size={20} />
                          </span>
                          <div style={{ flex: 1 }}>
                            <Skeleton.Input active size="small" style={{ width: "60%", height: 14 }} />
                          </div>
                        </li>
                        <li>
                          <span className="check-icon-circle" style={{ background: "transparent" }}>
                            <Skeleton.Avatar active shape="circle" size={20} />
                          </span>
                          <div style={{ flex: 1 }}>
                            <Skeleton.Input active size="small" style={{ width: "88%", height: 14 }} />
                          </div>
                        </li>
                        <li>
                          <span className="check-icon-circle" style={{ background: "transparent" }}>
                            <Skeleton.Avatar active shape="circle" size={20} />
                          </span>
                          <div style={{ flex: 1 }}>
                            <Skeleton.Input active size="small" style={{ width: "78%", height: 14 }} />
                          </div>
                        </li>
                        <li>
                          <span className="check-icon-circle" style={{ background: "transparent" }}>
                            <Skeleton.Avatar active shape="circle" size={20} />
                          </span>
                          <div style={{ flex: 1 }}>
                            <Skeleton.Input active size="small" style={{ width: "72%", height: 14 }} />
                          </div>
                        </li>
                      </ul>
                    </div>

                    <div className="status-stat-list">
                      <div className="stat-row">
                        <span className="label">
                          <Skeleton.Input active size="small" style={{ width: 90, height: 13 }} />
                        </span>
                        <span className="val">
                          <Skeleton.Input active size="small" style={{ width: 50, height: 13 }} />
                        </span>
                      </div>
                      <div className="stat-row">
                        <span className="label">
                          <Skeleton.Input active size="small" style={{ width: 90, height: 13 }} />
                        </span>
                        <span className="val">
                          <Skeleton.Input active size="small" style={{ width: 50, height: 13 }} />
                        </span>
                      </div>
                      <div className="stat-row">
                        <span className="label">
                          <Skeleton.Input active size="small" style={{ width: 115, height: 13 }} />
                        </span>
                        <span className="val">
                          <Skeleton.Input active size="small" style={{ width: 50, height: 13 }} />
                        </span>
                      </div>
                      <div className="stat-row">
                        <span className="label">
                          <Skeleton.Input active size="small" style={{ width: 100, height: 13 }} />
                        </span>
                        <span className="val">
                          <Skeleton.Input active size="small" style={{ width: 85, height: 13 }} />
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Card: Seat Counter & Calculation Skeleton */}
                  <div className="seat-selector-card" style={{ cursor: "default" }}>
                    <div>
                      <div className="selector-title">
                        <Skeleton.Input active size="small" style={{ width: 180, height: 18 }} />
                      </div>

                      <div className="counter-container" style={{ border: "none", background: "transparent" }}>
                        <Skeleton.Button active style={{ width: 44, height: 44, borderRadius: 8 }} />
                        <Skeleton.Button active style={{ width: 68, height: 44, borderRadius: 8, margin: "0 10px" }} />
                        <Skeleton.Button active style={{ width: 44, height: 44, borderRadius: 8 }} />
                      </div>

                      <div className="price-breakdown">
                        <div style={{ textAlign: "center", marginBottom: 10 }}>
                          <Skeleton.Input active size="small" style={{ width: 190, height: 12 }} />
                        </div>
                        <div style={{ textAlign: "center", marginBottom: 10 }}>
                          <Skeleton.Input active size="small" style={{ width: 140, height: 28 }} />
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <Skeleton.Input active size="small" style={{ width: 130, height: 12 }} />
                        </div>
                      </div>
                    </div>

                    <div>
                      <Skeleton.Button active block style={{ height: 48, borderRadius: 9, marginBottom: 14 }} />
                      <div style={{ textAlign: "center" }}>
                        <Skeleton.Input active size="small" style={{ width: 260, height: 12 }} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flow-cards-wrapper">
                  {/* Left Card: Plan Details */}
                  <div className="plan-info-card">
                    <div>
                      <span className="plan-badge-top">Official Trusted Dealer Plan</span>

                      <div className="plan-header">
                        <div className="plan-icon">
                          <IdcardOutlined />
                        </div>
                        <div>
                          <h3>{plan?.name || "Installer Pro Access"}</h3>
                          <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
                            Flexible monthly seat subscription
                          </p>
                        </div>
                      </div>

                      <ul className="feature-list">
                        <li>
                          <span className="check-icon-circle">
                            <CheckOutlined />
                          </span>
                          <span><strong>${unitPrice.toFixed(2)}</strong> per seat / month</span>
                        </li>
                        <li>
                          <span className="check-icon-circle">
                            <CheckOutlined />
                          </span>
                          <span>Billed monthly with automated recurring cycle</span>
                        </li>
                        <li>
                          <span className="check-icon-circle">
                            <CheckOutlined />
                          </span>
                          <span>Add or scale seats anytime as team grows</span>
                        </li>
                        <li>
                          <span className="check-icon-circle">
                            <CheckOutlined />
                          </span>
                          <span>Cancel auto-renewal anytime with 1-click</span>
                        </li>
                      </ul>
                    </div>

                    {isAddMoreMode && seatStatus && (
                      <div className="status-stat-list">
                        <div className="stat-row">
                          <span className="label">Current Quota</span>
                          <span className="val">{seatStatus?.seats?.total_seats ?? 0} Seats</span>
                        </div>
                        <div className="stat-row">
                          <span className="label">Currently Used</span>
                          <span className="val">{seatStatus?.seats?.used_seats ?? 0} Seats</span>
                        </div>
                        <div className="stat-row">
                          <span className="label">Available Right Now</span>
                          <span className="val" style={{ color: "#ef4444" }}>
                            {seatStatus?.seats?.remaining_seats ?? 0} Seats
                          </span>
                        </div>
                        <div className="stat-row">
                          <span className="label">Next Billing Date</span>
                          <span className="val" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <CalendarOutlined style={{ color: "#0284c7" }} />
                            {renewsOn}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Card: Seat Counter & Calculation */}
                  <div className="seat-selector-card">
                    <div>
                      <div className="selector-title">
                        {isAddMoreMode ? "How many seats to add?" : "Number of Installer Seats"}
                      </div>

                      {/* Counter */}
                      <div className="counter-container">
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={handleDecrement}
                          disabled={seatCount <= 1}
                          title="Decrease seats"
                        >
                          <MinusOutlined style={{ fontSize: 16 }} />
                        </button>
                        <input
                          type="number"
                          className="counter-input"
                          value={seatCount}
                          onChange={handleInputChange}
                          min="1"
                        />
                        <button
                          type="button"
                          className="counter-btn"
                          onClick={handleIncrement}
                          title="Increase seats"
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                        </button>
                      </div>

                      {/* Price Breakdown */}
                      <div className="price-breakdown">
                        <div className="label">
                          {isAddMoreMode ? "Additional Monthly Charge" : "Total Subscription Price"}
                        </div>
                        <div className="total-price">
                          ${totalPrice} <span className="interval">/ month</span>
                        </div>
                        <div className="formula">
                          {seatCount} {seatCount === 1 ? "seat" : "seats"} × ${unitPrice.toFixed(2)} USD
                        </div>
                      </div>
                    </div>

                    <div>
                      {/* Checkout Button */}
                      <button
                        type="button"
                        className="checkout-btn"
                        onClick={handleProceedToCheckout}
                        disabled={checkoutLoading}
                      >
                        {checkoutLoading ? (
                          <>
                            <LoadingOutlined /> Connecting Stripe...
                          </>
                        ) : (
                          <>
                            <CreditCardOutlined style={{ fontSize: 18 }} />
                            Continue to Checkout
                          </>
                        )}
                      </button>

                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "12px",
                          fontSize: "12px",
                          color: "#94a3b8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                        }}
                      >
                        <LockOutlined style={{ color: "#10b981" }} />
                        <span>Guaranteed 256-bit encrypted checkout via Stripe</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Screen 3: Stripe Checkout Redirecting Modal */}
      <Modal
        open={checkoutLoading}
        footer={null}
        closable={false}
        centered
        width={440}
      >
        <div style={{ textAlign: "center", padding: "28px 16px" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "16px",
              background: "linear-gradient(135deg, #4f46e5 0%, #0284c7 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "28px",
              margin: "0 auto 18px auto",
              boxShadow: "0 8px 20px rgba(2, 132, 199, 0.3)",
            }}
          >
            <CreditCardOutlined />
          </div>
          <h3 style={{ fontSize: "19px", fontWeight: 600, margin: "0 0 6px 0", color: "#0f172a" }}>
            Complete Your Purchase
          </h3>
          <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "22px" }}>
            You are being securely redirected to Stripe to finalize payment.
          </p>

          <div
            style={{
              background: "#f8fafc",
              borderRadius: "14px",
              padding: "16px 20px",
              textAlign: "left",
              marginBottom: "24px",
              fontSize: "14px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ color: "#64748b" }}>Plan</span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>{plan?.name || "Installer Pro Access"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ color: "#64748b" }}>Selected Seats</span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>{seatCount}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ color: "#64748b" }}>Price per seat</span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>${unitPrice.toFixed(2)} / month</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1.5px dashed #cbd5e1",
                paddingTop: "10px",
                marginTop: "8px",
                fontWeight: 600,
                fontSize: "16px",
              }}
            >
              <span>Total Amount</span>
              <span style={{ color: "#0284c7" }}>${totalPrice} / month</span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              padding: "10px 16px",
              borderRadius: "8px",
              background: "#f0f9ff",
              color: "#0369a1",
            }}
          >
            <Spin indicator={<LoadingOutlined style={{ fontSize: 20, color: "#0284c7" }} spin />} />
            <span style={{ fontSize: "14px", fontWeight: 600 }}>
              Redirecting to secure Stripe checkout...
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChooseInstallerSeats;
